import { cn } from '@lib/utils';
import Image from 'next/image';
import React, { useEffect } from 'react';

import CatConventionalPlan from '@assets/images/cat-conventional-plan.svg';
import CatTakafulPlan from '@assets/images/cat-takaful-plan.svg';
import { GetStaticProps } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { ENABLE_TAKAFUL } from '@utils/constant';

interface IProductChooserProps {
  typeOfPurchase: 'add-pet' | 'eligibility';
}

const ProductChooser: React.FC<IProductChooserProps> = () => {
  const { t: tc } = useTranslation('common');
  const router = useRouter();
  const typeOfPurchase = router.query.typeOfPurchase as
    | 'add-pet'
    | 'eligibility';
  const promoRef =
    router.query.promo && router.query.promo !== 'undefined'
      ? `?promo=${router.query.promo}`
      : '';

  const showTakaful = ENABLE_TAKAFUL;

  const conventionalLink =
    typeOfPurchase === 'add-pet'
      ? `/add-pet/new${promoRef}`
      : `/eligibility/v4`;

  const takafulLink =
    typeOfPurchase === 'add-pet'
      ? `/add-pet/takaful/new${promoRef}`
      : `/eligibility/takaful/petDetails${promoRef}`;

  useEffect(() => {
    router.push('/eligibility/v4');
  }, []);

  return (
    <div className="w-100 h-[80vh] flex justify-center items-center">
      <div className="max-w-xl bg-white rounded-[10px] pb-4">
        <div className="w-100 flex justify-start px-4 py-2 border-b-[1px] border-gray-300">
          <p className="text-lg font-bold">{tc('choose_a_product')}</p>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between p-4 gap-4">
          <div
            className={cn(
              'pet-card cursor-pointer flex flex-col items-center p-6 gap-4 border-[1px]'
            )}
            onClick={() => {
              window.location.href = conventionalLink;
              router.push(conventionalLink, undefined, {
                locale: router.locale || 'en',
              });
            }}
          >
            <div className="flex-shrink-0 w-[60px] md:w-[80px] mr-3">
              <Image
                src={CatConventionalPlan}
                height={48}
                width={48}
                layout="responsive"
              />
            </div>

            <div className="flex flex-col flex-grow justify-center items-center">
              <p className="text-lg font-bold">{tc('pet_insurance')}</p>
              <p className="text-md font-normal text-center">
                {tc('get_covered_content')}
              </p>
            </div>
          </div>

          {showTakaful ? (
            <div
              className={cn(
                'pet-card cursor-pointer flex flex-col items-center p-6 gap-4 border-[1px]'
              )}
              onClick={() => {
                router.push(takafulLink, undefined, {
                  locale: router.locale === 'en' ? 'eni' : 'myi',
                });
              }}
            >
              <div className="flex-shrink-0 w-[60px] md:w-[80px] mr-3">
                <Image src={CatTakafulPlan} layout="responsive" />
              </div>

              <div className="flex flex-col flex-grow justify-center items-center">
                <p className="text-lg font-bold">{tc('pet_takaful')}</p>
                <p className="text-md font-normal text-center">
                  {tc('pet_takaful_content')}
                </p>
              </div>
            </div>
          ) : null}
        </div>

        <div className="flex flex-row justify-center items-center w-100">
          <div className="font-small">
            <Link href="https://www.oyen.my">
              <a className="text-gray-400 underline hover:text-black">
                {tc('back_to_homepage')}
              </a>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export const getStaticProps: GetStaticProps = async ({ locale = '' }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common', 'dashboard'])),
    },
  };
};

export default ProductChooser;
